import { createRouter, createWebHashHistory } from 'vue-router'
import store from '@/store'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css' // progress bar style
import noticeRoute from '@/router/modules/notice'
import accountManageRoute from '@/router/modules/accountManage'
import { hydrateStoreFromSharedAuth } from '@/util/shared-auth'
// 固定路由
const constantRoutes = [
    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/login.vue'),
        meta: {
            title: 'Login',
            i18n: 'route.login'
        }
    },
    {
        path: '/',
        name: 'home',
        component: () => import('@/views/home/index.vue'),
        meta: {
            title: 'Home',
        }
    },
    {
        path: '/home',
        redirect: '/'
    },
    {
        path: '/pay/:payload?',
        name: 'pricePay',
        component: () => import('@/views/pay.vue'),
        meta: {
            title: 'Payment',
            i18n: 'route.pricePay'
        }
    },
    {
        path: '/show_word',
        name: 'show_word',
        component: () => import('@/views/show_word.vue'),
        meta: {
            title: 'Show',
            i18n: 'route.showWord',
            hideHeader: true
        }
    },
    {
        path: '/about',
        name: 'about',
        component: () => import('@/views/about.vue'),
        meta: {
            title: 'About Us',
        }
    },
    {
        path: '/authorize',
        name: 'authorize',
        component: () => import('@/views/authorize.vue'),
        meta: {
            title: () => store.state.settings.authorizeTitle,
            i18n: 'route.authorize',
            hideHeader: true
        }
    },
    {
        path: '/authLogin',
        name: 'authLogin',
        component: () => import('@/views/authLogin.vue'),
        meta: {
            title: 'Login',
            i18n: 'route.authLogin'
        }
    },
    {
        path: '/authLogout',
        name: 'authLogout',
        component: () => import('@/views/authLogout.vue'),
        meta: {
            title: () => store.state.settings.authLogoutTitle,
            i18n: 'route.authLogout',
            hideHeader: true
        }
    },
    {
        path: '/policyHall',
        name: 'policyHall',
        component: () => import('@/views/policyHall.vue'),
        meta: {
            title: '用户协议'
        }
    },
    {
        path: '/policyHallDetail',
        name: 'policyHallDetail',
        component: () => import('@/views/policyHallDetail.vue'),
        meta: {
            title: '隐私协议'
        }
    },
    noticeRoute,
    accountManageRoute
]
// 免登录白名单
const noLoginWhitelist = ['/', '/login', '/home', '/policyHall', '/policyHallDetail', '/show_word', '/authorize', '/authLogin', '/authLogout']
// 动态路由（异步路由、导航栏路由）
const asyncRoutes = []
const lastRoute = {
    path: '/:pathMatch(.*)*',
    component: () => import('@/views/404.vue'),
    meta: {
        title: 'Page Not Found'
    }
}

const router = createRouter({
    history: createWebHashHistory(),
    routes: constantRoutes,
    scrollBehavior() {
        // 始终滚动到顶部，不恢复之前的位置
        return { top: 0 }
    }
})

// 禁用浏览器刷新时的自动滚动恢复
if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual'
}

router.beforeEach(async(to, from, next) => {
    store.state.settings.enableProgress && NProgress.start()
    hydrateStoreFromSharedAuth(store)
    // 判断页面是否需要登录
    if (to.name === 'pricePay' || noLoginWhitelist.includes(to.path)) {
        next()
        return
    }
    // 判断是否登录
    if (!store.getters['user/isLogin']) {
        next({
            name: 'login',
            query: {
                redirect: to.fullPath
            }
        })
        return
    }
    // 只有在用户已登录且尚未生成路由时获取权限
    // if (store.getters['user/isLogin'] && !store.state.menu.isGenerate) {
    //     await store.dispatch('user/getPermissions');
    // }
    // 是否已根据权限动态生成并挂载路由
    if (store.state.menu.isGenerate) {
        // 导航栏如果不是 single 模式，则需要根据 path 定位主导航的选中状态
        store.state.settings.menuMode !== 'single' &&
            store.commit('menu/setHeaderActived', to.path, to.meta)
        if (to.name) {
            if (to.matched.length !== 0) {
                // 如果已登录状态下，进入登录页会强制跳转到控制台页面
                if (to.name == 'login') {
                    next({
                        name: 'nav',
                        replace: true
                    })
                } else if (!store.state.settings.enableDashboard && to.name == 'dashboard') {
                    // 如果未开启控制台页面，则默认进入第一个固定标签栏或者侧边栏导航第一个模块
                    if (
                        store.state.settings.enableTabbar &&
                        store.state.tabbar.list.some(v => v.isPin)
                    ) {
                        let fullPath
                        for (let i = 0; i < store.state.tabbar.list.length; i++) {
                            if (store.state.tabbar.list[i].isPin) {
                                fullPath = store.state.tabbar.list[i].fullPath
                                break
                            }
                        }
                        next({
                            path: fullPath,
                            replace: true
                        })
                    } else if (store.getters['menu/sidebarRoutes'].length > 0) {
                        next({
                            path: store.getters['menu/sidebarRoutesFirstDeepestPath'],
                            replace: true
                        })
                    } else {
                        next()
                    }
                } else {
                    next()
                }
            } else {
                // 如果是通过 name 跳转，并且 name 对应的路由没有权限时，需要做这步处理，手动指向到 404 页面
                next({
                    path: '/404'
                })
            }
        } else {
            next()
        }
    } else {
        // 挂载动态路由的同时，根据当前帐号复原固定标签栏
        store.state.settings.enableTabbar &&
            store.commit('tabbar/recoveryStorage', store.state.user.account)
        let accessRoutes = []
        if (!store.state.settings.enableBackendReturnRoute) {
            accessRoutes = await store.dispatch('menu/generateRoutesAtFront', asyncRoutes)
        } else {
            accessRoutes = await store.dispatch('menu/generateRoutesAtBack')
        }
        accessRoutes.push(lastRoute)
        let removeRoutes = []
        accessRoutes.forEach(route => {
            if (!/^(https?:|mailto:|tel:)/.test(route.path)) {
                removeRoutes.push(router.addRoute(route))
            }
        })
        // 记录的 accessRoutes 路由数据，在登出时会使用到，不使用 router.removeRoute 是考虑配置的路由可能不一定有设置 name ，则通过调用 router.addRoute() 返回的回调进行删除
        store.commit('menu/setCurrentRemoveRoutes', removeRoutes)
        next({ ...to, replace: true })
    }
})

router.afterEach((to, from) => {
    store.state.settings.enableProgress && NProgress.done()
    // 强制滚动到顶部，防止浏览器恢复滚动位置
    window.scrollTo(0, 0)
    // 设置页面 title
    let isSetTitle = false
    if (store.state.settings.enableTabbar) {
        const tabId = store.state.settings.enableTabbarMergeTabs
            ? to.meta.activeMenu || to.fullPath
            : to.fullPath
        if (store.state.tabbar.list.some(item => item.tabId === tabId)) {
            store.state.tabbar.list.map(item => {
                if (item.tabId === tabId) {
                    store.dispatch('settings/setTitle', item.title)
                }
            })
            isSetTitle = true
        }
    }
    !isSetTitle &&
        to.meta.title &&
        store.dispatch(
            'settings/setTitle',
            typeof to.meta.title === 'function' ? to.meta.title() : to.meta.title
        )
    // 判断当前页面是否开启缓存，如果开启，则将当前页面的 name 信息存入 keep-alive 全局状态
    if (to.meta.cache) {
        let componentName = to.matched[to.matched.length - 1].components.default.name
        if (componentName) {
            store.commit('keepAlive/add', componentName)
        } else {
            console.warn('该页面组件未设置组件名，会导致缓存失效，请检查')
        }
    }
    // 判断离开页面是否开启缓存，如果开启，则根据缓存规则判断是否需要清空 keep-alive 全局状态里离开页面的 name 信息
    if (from.meta.cache) {
        let componentName = from.matched[from.matched.length - 1].components.default.name
        // 通过 meta.cache 判断针对哪些页面进行缓存
        switch (typeof from.meta.cache) {
            case 'string':
                if (from.meta.cache != to.name) {
                    store.commit('keepAlive/remove', componentName)
                }
                break
            case 'object':
                if (!from.meta.cache.includes(to.name)) {
                    store.commit('keepAlive/remove', componentName)
                }
                break
        }
        // 通过 meta.noCache 判断针对哪些页面不需要进行缓存
        if (from.meta.noCache) {
            switch (typeof from.meta.noCache) {
                case 'string':
                    if (from.meta.noCache == to.name) {
                        store.commit('keepAlive/remove', componentName)
                    }
                    break
                case 'object':
                    if (from.meta.noCache.includes(to.name)) {
                        store.commit('keepAlive/remove', componentName)
                    }
                    break
            }
        }
        // 如果进入的是 reload 页面，则也将离开页面的缓存清空
        if (to.name == 'reload') {
            store.commit('keepAlive/remove', componentName)
        }
    }
})

export default router
